import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Sparkle, ShieldCheck, MapPin, GraduationCap, ArrowRight, Sun,
  Palette, X as CloseX, Check,
} from "@phosphor-icons/react";

/**
 * YouthProfile — the canonical public page for a SocialPulse YouthID.
 *
 * Reachable at `/y/<code>` on every SocialPulse site (NaturePulse,
 * FarmersPulse, AlumniPulse, IndiaAlumni) — the backend endpoint
 * `/api/youth/by-code/<code>` returns only the public-safe projection.
 *
 * The design rule is: the public never has enough information to identify
 * the child. This page therefore renders only:
 *   - First name or alias
 *   - Age band (never exact age)
 *   - Region (never precise address)
 *   - School name only if the school has opted-in
 *   - Public role + badges + contribution count
 *   - Verified chips (School Verified / Guardian Approved)
 */
const BAND_LABEL = {
  under_8: "Under 8",
  "8_10":  "Age Group 8–10",
  "11_13": "Age Group 11–13",
  "14_15": "Age Group 14–15",
  "16_17": "Age Group 16–17",
  adult:   "Adult",
};

export default function YouthProfile() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [disc, setDisc] = useState(null);
  const [err, setErr] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [saveState, setSaveState] = useState({ status: "idle", msg: "" });

  // Which SocialPulse site is rendering? Resolved (in priority order):
  // 1. Explicit build-time env  REACT_APP_SITE_ID
  // 2. Hostname sniff (farmerspulse.com → farmerspulse etc.)
  // 3. Default NaturePulse pack
  const siteId = (() => {
    if (process.env.REACT_APP_SITE_ID) return process.env.REACT_APP_SITE_ID;
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (/farmerspulse/i.test(host)) return "farmerspulse";
    if (/indiaalumni/i.test(host))  return "indiaalumni";
    if (/alumnipulse/i.test(host))  return "alumnipulse";
    return "naturepulse";
  })();

  useEffect(() => {
    if (!code) return;
    http.get(`/youth/by-code/${encodeURIComponent(code)}`)
      .then((r) => setData(r.data.public))
      .catch((e) => {
        if (e?.response?.status === 404) {
          setErr("This Youth Code doesn't exist — or was retired.");
        } else {
          setErr("We couldn't reach the SocialPulse network. Please try again shortly.");
        }
      });
  }, [code]);

  useEffect(() => {
    http.get(`/youth/disclaimer/${siteId}`)
      .then((r) => setDisc(r.data.disclaimer))
      .catch(() => { /* silent — page still works without the pack */ });
  }, [siteId]);

  // Deferred avatar catalog — only fetched when the picker opens.
  useEffect(() => {
    if (!pickerOpen || catalog) return;
    http.get("/youth/avatars")
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog({ heads: [], expressive: [], vibe: [] }));
  }, [pickerOpen, catalog]);

  const activePick = data?.avatar
    ? { variant: data.avatar.variant, index: data.avatar.index }
    : null;

  const pickAvatar = async (variant, index) => {
    // Optimistic UI: swap the displayed avatar immediately.
    const nextUrl =
      variant === "expressive" ? `/silhouettes/expressive-${String(index).padStart(2, "0")}.png` :
      variant === "vibe"       ? `/silhouettes/vibe-${String(index).padStart(2, "0")}.png` :
                                 `/silhouettes/head-${String(index).padStart(2, "0")}.png`;
    setData((d) => d && ({
      ...d,
      avatar: {
        ...(d.avatar || {}),
        variant, index,
        head_url: nextUrl,
        body_url: `/silhouettes/body-${String(index).padStart(2, "0")}.png`,
      },
    }));
    setSaveState({ status: "saving", msg: "" });

    // Try to save for real — the steward endpoint gives us the person_id
    // AND authorises the PATCH in one round-trip. If we're not a steward,
    // it 401/403's and we degrade gracefully to a preview.
    try {
      const lookup = await http.get(`/youth/steward/by-code/${encodeURIComponent(code)}`);
      const personId = lookup.data?.youth?.id;
      if (!personId) throw new Error("No person id");
      await http.patch(`/youth/${personId}`, {
        avatar_variant: variant,
        avatar_index: index,
      });
      setSaveState({ status: "saved", msg: "Saved to this student's profile." });
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        setSaveState({
          status: "preview",
          msg: "Preview only — ask your teacher or parent to save this from their steward console.",
        });
      } else {
        setSaveState({ status: "error", msg: "Couldn't save right now — please try again." });
      }
    }
  };

  if (err) {
    return (
      <main className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] pt-36 md:pt-40 pb-20 px-5" data-testid="youth-profile-404">
        <div className="max-w-xl mx-auto text-center">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Youth Code
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl">Not found</h1>
          <p className="mt-3 text-[#3A3830]">{err}</p>
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            <Link
              to="/"
              data-testid="youth-404-home"
              className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
            >
              Explore NaturePulse
            </Link>
            <Link
              to="/about/youth-privacy"
              data-testid="youth-404-policy"
              className="rounded-full border border-black/20 text-[#1B4332] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
            >
              What is a Youth Code?
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] pt-36 md:pt-40 pb-16 px-5"
      data-testid="youth-profile-page"
    >
      <div className="max-w-3xl mx-auto">
        {/* Eyebrow */}
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <Sparkle size={12} weight="fill" className="inline mr-1" /> SocialPulse YouthID
        </div>

        {/* Header row: avatar + name */}
        <div className="mt-2 flex items-start gap-5">
          <div
            className="shrink-0 w-24 h-24 md:w-32 md:h-32 rounded-full bg-[#F0E4CE] border border-black/10 overflow-hidden flex items-center justify-center"
            data-testid="youth-profile-avatar"
          >
            {data?.avatar?.head_url && (
              <img
                src={data.avatar.head_url}
                alt="Anonymous silhouette"
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% 22%" }}
                loading="lazy"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight"
              data-testid="youth-profile-name"
            >
              {data ? data.first_name : (
                <span
                  data-testid="youth-profile-skeleton-name"
                  className="inline-block h-10 md:h-12 w-56 rounded bg-black/5 animate-pulse align-middle"
                  aria-label="Loading"
                />
              )}
              {data && (
                <span className="ml-3 text-[color:var(--np-brand,#B85F2A)] align-baseline text-2xl md:text-3xl font-mono-tech tracking-[0.12em]">
                  {data.youth_code}
                </span>
              )}
            </h1>
            {!data && (
              <div className="mt-3 flex flex-col gap-2" data-testid="youth-profile-skeleton">
                <span className="inline-block h-4 w-40 rounded bg-black/5 animate-pulse" />
                <span className="inline-block h-24 w-full max-w-lg rounded-2xl bg-black/5 animate-pulse" />
              </div>
            )}
            {data && (
              <>
                <p
                  className="mt-1 font-serif-display italic text-lg text-[#3A3830]"
                  data-testid="youth-profile-role"
                >
                  {data.public_role}
                </p>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  data-testid="youth-choose-face-btn"
                  className="mt-2 inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#1B4332] hover:bg-black/5"
                >
                  <Palette size={11} weight="fill" /> Choose your face
                </button>
              </>
            )}
          </div>
        </div>

        {/* Trust chips */}
        {data && (
          <div className="mt-4 flex flex-wrap gap-2">
            {data.verified?.school && (
              <span
                data-testid="youth-badge-school"
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase"
              >
                <ShieldCheck size={11} weight="fill" /> School Verified
              </span>
            )}
            {data.verified?.guardian && (
              <span
                data-testid="youth-badge-guardian"
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase"
              >
                <ShieldCheck size={11} weight="fill" /> Guardian Approved
              </span>
            )}
          </div>
        )}

        {/* Meta card — age band, location, school */}
        {data && (
          <section
            className="mt-6 rounded-3xl border border-black/10 bg-white p-5 grid sm:grid-cols-2 gap-4"
            data-testid="youth-profile-meta"
          >
            <Meta icon={Sun} label="Age Group" value={BAND_LABEL[data.age_band] || data.age_band} />
            <Meta icon={MapPin} label="Region"
                  value={[data.region, data.country].filter(Boolean).join(", ") || "—"} />
            {data.school_name && (
              <Meta
                icon={GraduationCap} label="School"
                value={data.school_group ? `${data.school_name} · ${data.school_group}` : data.school_name}
              />
            )}
            <Meta
              icon={Sparkle} label="Contributions"
              value={`${data.contribution_count} observation${data.contribution_count === 1 ? "" : "s"}`}
            />
          </section>
        )}

        {/* Badges */}
        {data && data.badges?.length > 0 && (
          <section className="mt-8" data-testid="youth-profile-badges">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              Badges earned
            </div>
            <h2 className="mt-1 font-serif-display text-2xl">Every observation is a small triumph.</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {data.badges.map((b) => (
                <div
                  key={b.slug}
                  data-testid={`youth-badge-${b.slug}`}
                  className="rounded-2xl border border-black/10 bg-white p-3 flex items-center gap-2"
                >
                  <span className="text-2xl" aria-hidden>{b.emoji}</span>
                  <div className="font-serif-display leading-tight">{b.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Privacy notice — site-specific pack */}
        <section
          className="mt-10 rounded-3xl bg-[#1B4332] text-[#F5EBDC] p-6"
          data-testid="youth-profile-privacy"
        >
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <ShieldCheck size={12} weight="fill" className="inline mr-1" />
            {disc?.eyebrow || "Youth Privacy Zone"}
          </div>
          <h3 className="mt-1 font-serif-display text-2xl" data-testid="youth-profile-privacy-title">
            {disc?.title || "Protected by SocialPulse"}
          </h3>
          <p className="mt-2 max-w-xl text-[#E7DDCA]" data-testid="youth-profile-privacy-body">
            {disc?.body ||
              "This page never shows a minor's full name, date of birth, exact age, precise address or family details. Locations are rounded, photos are EXIF-stripped, and no adult can privately message a Youth Code. Every activity is logged through a school or guardian steward."}
          </p>
          {disc?.citations?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="youth-profile-privacy-citations">
              {disc.citations.map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-white/10 text-[#E7DDCA] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          <Link
            to={disc?.policy_url || "/about/youth-privacy"}
            data-testid="youth-profile-privacy-link"
            className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            Read the full policy <ArrowRight size={10} weight="bold" />
          </Link>
          {disc?.brand && (
            <div className="mt-3 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#E7DDCA]/70">
              {disc.brand} · Youth Privacy Pack
            </div>
          )}
        </section>

        {/* Footer note */}
        {data?.member_since && (
          <div className="mt-6 text-center font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
            Member since {data.member_since}
          </div>
        )}
      </div>

      {pickerOpen && (
        <AvatarPicker
          catalog={catalog}
          active={activePick}
          onPick={pickAvatar}
          onClose={() => setPickerOpen(false)}
          saveState={saveState}
        />
      )}
    </main>
  );
}

// --------------------------------------------------------------------------
//  Avatar Picker — modal grid of all 30 anonymous faces, grouped by variant.
//  Public users can preview any face on the profile; only stewards can save
//  the choice permanently (server enforces auth on the PATCH endpoint).
// --------------------------------------------------------------------------
function AvatarPicker({ catalog, active, onPick, onClose, saveState }) {
  const groups = useMemo(() => {
    if (!catalog) return [];
    return [
      { key: "silhouette", label: "Silhouette",  items: catalog.heads      || [] },
      { key: "expressive", label: "Expressive",  items: catalog.expressive || [] },
      { key: "vibe",       label: "Vibe",        items: catalog.vibe       || [] },
    ];
  }, [catalog]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#1B4332]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
      data-testid="avatar-picker"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#F5EBDC] p-5 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Palette size={11} weight="fill" className="inline mr-1" /> Pick your anonymous face
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">
              Choose from 30 anonymous faces.
            </h2>
            <p className="mt-1 text-sm text-[#3A3830] max-w-lg">
              Nobody in the public can identify you from any of these — they're
              shared with lots of other young observers. Pick the one that
              feels most like you today.
            </p>
            <p
              className="mt-2 text-xs text-[#3A3830] max-w-lg"
              data-testid="avatar-picker-safety-note"
            >
              <strong>Only these faces are allowed.</strong> Uploading a real
              photo or pasting a picture from the internet is not possible on
              YouthID — this keeps every young observer anonymous by design.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="avatar-picker-close"
            className="shrink-0 rounded-full bg-white border border-black/10 p-1.5 hover:bg-black/5"
            aria-label="Close"
          >
            <CloseX size={14} weight="bold" />
          </button>
        </div>

        {saveState?.status !== "idle" && (
          <div
            data-testid="avatar-picker-toast"
            className={`mt-4 rounded-2xl px-3 py-2 text-sm ${
              saveState.status === "preview" ? "bg-amber-100 text-amber-900" :
              saveState.status === "saving"  ? "bg-black/5 text-[#3A3830]" :
              saveState.status === "error"   ? "bg-red-100 text-red-900" :
                                               "bg-emerald-100 text-emerald-900"
            }`}
          >
            {saveState.status === "saving" ? "Saving your pick…" : saveState.msg}
          </div>
        )}

        {!catalog && (
          <div className="mt-6 grid grid-cols-4 sm:grid-cols-6 gap-2">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="aspect-square rounded-2xl bg-black/5 animate-pulse" />
            ))}
          </div>
        )}

        {groups.map((g) => (
          <section key={g.key} className="mt-6" data-testid={`avatar-group-${g.key}`}>
            <div className="flex items-baseline justify-between">
              <h3 className="font-serif-display text-lg">{g.label}</h3>
              <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
                {g.items.length} faces
              </span>
            </div>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {g.items.map((it) => {
                const selected = active?.variant === g.key && active?.index === it.index;
                return (
                  <button
                    key={it.index}
                    type="button"
                    onClick={() => onPick(g.key, it.index)}
                    data-testid={`avatar-tile-${g.key}-${it.index}`}
                    className={`relative aspect-square rounded-2xl overflow-hidden border transition-all ${
                      selected
                        ? "border-[color:var(--np-brand,#B85F2A)] ring-2 ring-[color:var(--np-brand,#B85F2A)]/40"
                        : "border-black/10 hover:border-black/30"
                    } bg-[#F0E4CE]`}
                    aria-label={`${g.label} face ${it.index}`}
                  >
                    <img
                      src={it.url}
                      alt=""
                      className="w-full h-full object-cover"
                      style={{ objectPosition: "50% 22%" }}
                      loading="lazy"
                    />
                    {selected && (
                      <span className="absolute top-1 right-1 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white p-0.5">
                        <Check size={10} weight="bold" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="avatar-picker-done"
            className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[color:var(--np-brand,#B85F2A)]">
        <Icon size={13} weight="fill" />
        <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
          {label}
        </div>
      </div>
      <div className="mt-1 font-serif-display text-xl">{value}</div>
    </div>
  );
}
